export type ParsedImportRow = {
  classroom_id?: number;
  classroom_name?: string;
  description?: string | null;
  teacher_id?: number | null;
  student_ids?: number[];
};

export type ParsedImportError = {
  row: number;
  reason: string;
};

const toPositiveInt = (value: string): number | null => {
  const parsed = Number(value.trim());
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed;
  }
  return null;
};

export const parseStudentIds = (value: string): number[] => {
  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  const tokens = normalized
    .split(/[|;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  const ids = new Set<number>();
  for (const token of tokens) {
    const parsed = toPositiveInt(token);
    if (parsed !== null) {
      ids.add(parsed);
    }
  }

  return [...ids];
};

export const splitCsvLine = (line: string): string[] => {
  const values: string[] = [];
  let current = "";
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = index + 1 < line.length ? line[index + 1] : "";

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values.map((value) => value.trim());
};

const parseOptionalId = (
  rowNumber: number,
  fieldName: string,
  rawValue: string | undefined,
): { value?: number; error?: ParsedImportError } => {
  if (rawValue === undefined || rawValue === "") {
    return {};
  }

  const parsed = toPositiveInt(rawValue);
  if (parsed === null) {
    return {
      error: {
        row: rowNumber,
        reason: `${fieldName} must be a positive integer`,
      },
    };
  }

  return { value: parsed };
};

export function parseClassroomImportCsv(csvText: string): {
  rows: ParsedImportRow[];
  errors: ParsedImportError[];
} {
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return {
      rows: [],
      errors: [{ row: 0, reason: "CSV is empty" }],
    };
  }

  const headers = splitCsvLine(lines[0] || "").map((header) =>
    header.toLowerCase(),
  );
  const indexByHeader = new Map<string, number>();
  headers.forEach((header, index) => {
    indexByHeader.set(header, index);
  });

  const rows: ParsedImportRow[] = [];
  const errors: ParsedImportError[] = [];

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    if (!line) {
      continue;
    }

    const rowNumber = lineIndex + 1;
    const values = splitCsvLine(line);
    const getValue = (header: string): string | undefined => {
      const index = indexByHeader.get(header);
      return index === undefined ? undefined : (values[index] ?? "");
    };

    const classroomIdRaw = getValue("classroom_id");
    const teacherIdRaw = getValue("teacher_id");
    const classroomNameRaw = getValue("classroom_name");
    const descriptionRaw = getValue("description");
    const studentIdsRaw = getValue("student_ids");

    const classroomIdResult = parseOptionalId(
      rowNumber,
      "classroom_id",
      classroomIdRaw,
    );
    if (classroomIdResult.error) {
      errors.push(classroomIdResult.error);
      continue;
    }

    const teacherIdResult = parseOptionalId(rowNumber, "teacher_id", teacherIdRaw);
    if (teacherIdResult.error) {
      errors.push(teacherIdResult.error);
      continue;
    }

    const classroomName =
      typeof classroomNameRaw === "string" && classroomNameRaw.trim()
        ? classroomNameRaw.trim()
        : undefined;
    const description =
      descriptionRaw === undefined ? undefined : descriptionRaw.trim();
    const studentIds = studentIdsRaw ? parseStudentIds(studentIdsRaw) : undefined;

    if (!classroomIdResult.value && !classroomName) {
      errors.push({
        row: rowNumber,
        reason: "Each row requires classroom_id or classroom_name",
      });
      continue;
    }

    rows.push({
      classroom_id: classroomIdResult.value,
      classroom_name: classroomName,
      description,
      teacher_id: teacherIdResult.value,
      student_ids: studentIds,
    });
  }

  return { rows, errors };
}
